import React from 'react';
import { Link } from 'react-router-dom';
import { ProductDesignPageHeader, ProductDesignSection, SwatchChip } from './ProductDesignDocPrimitives';
import { DataTable, type Column } from '../../components/ds';
import { TOKEN_GROUPS, type TokenRow } from './designTokens.generated';

/**
 * The semantic-layer table used to hand-list eight tokens with its own role
 * wording — a third hand-maintained copy of the token roles, alongside the ones
 * that had already gone stale on the token page. It now renders the SAME
 * generated rows, so it cannot drift from index.css or from the canonical page.
 */
const SEMANTIC_COLUMNS: Column<TokenRow>[] = [
  {
    key: 'token',
    header: 'Token',
    width: '26%',
    render: (r) => <span className="whitespace-nowrap font-mono text-[var(--mustard)]">{r.token}</span>,
  },
  { key: 'role', header: 'Role' },
];

/** Surfaces + accents: the layer to reach for when composing a new widget. */
const SEMANTIC_ROWS: TokenRow[] = TOKEN_GROUPS.filter(
  (g) => g.id === 'surfaces' || g.id === 'accents'
).flatMap((g) => g.rows);

/** The two palettes and the job of each — the one place this is stated. */
const PALETTES: { name: string; job: string }[] = [
  {
    name: 'Paper',
    job: 'Light mode, everywhere: the public site, the app, and diagrams when shown in light. The site\'s stylesheet is the source and the app follows it. Ground #FAFAF7, ink #1E2A44, gold #A87400 for display and #946500 for small text, clay #A85443, forest #3E6B52, slate #5A6E8C.',
  },
  {
    name: 'Night',
    job: 'Dark mode: the app, the diagram canvas, the exhibition. Ground #0B0F19, text #D8DDE8, gold #D4A017, clay #C0533A, forest #3A7D56, slate #3D4B6F.',
  },
];

export const PrioritiesAIColorsPage: React.FC = () => {
  return (
    <>
      <ProductDesignPageHeader
        title="Colors"
        eyebrow="Foundations · tokens"
        subtitle={
          <>
            Two palettes, each with one job. Paper is light mode. Night is dark mode. New work
            uses the semantic tokens, which carry the Paper and Night values. Older colors are
            deprecated and live on the Legacy page.
          </>
        }
      />

      <ProductDesignSection
        title="Which palette is for what"
        hint="If two colors seem to compete, this table settles it."
      >
        <div className="border border-[var(--rule)]">
          {PALETTES.map((row) => (
            <div key={row.name} className="flex flex-col gap-1 border-b border-[var(--rule)] p-4 last:border-b-0 sm:flex-row sm:gap-8">
              <span className="w-28 shrink-0 font-serif text-[17px] text-[var(--text-pri)]">{row.name}</span>
              <span className="text-sm leading-relaxed text-[var(--text-sec)]">{row.job}</span>
            </div>
          ))}
        </div>
        <p className="mt-5 text-sm leading-relaxed text-[var(--text-sec)]">
          In Night, slate and forest are for lines and fills. As words they are too dim on the
          dark ground (2.2 to 1 and 3.9 to 1) — set quiet words in{' '}
          <code className="font-mono text-[0.85em] text-[var(--mustard)]">--text-ter</code> instead.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-[var(--text-sec)]">
          Two more colors belong to diagrams only, on the dark canvas:
        </p>
        <div className="mt-3 flex max-w-xs gap-2">
          <SwatchChip hex="#584E47" title="Brown · what already exists" />
          <SwatchChip hex="#9CA3AF" title="Fog gray · examples" />
        </div>
      </ProductDesignSection>

      <ProductDesignSection
        title="Current · the semantic tokens (Paper and Night)"
        hint="Use these for all new work. Each token has a light value (Paper) and a dark value (Night); the Token system page shows both."
      >
        <DataTable
          columns={SEMANTIC_COLUMNS}
          rows={SEMANTIC_ROWS}
          getRowKey={(r) => r.token}
          density="compact"
          bordered={false}
          caption="Semantic tokens"
          captionHidden
        />
        <p className="mt-4 text-sm leading-relaxed text-[var(--text-sec)]">
          These rows are the generated ones — the same source the{' '}
          <Link to="/elevation/priorities-ai-design-language/token-system" className="text-[var(--p-gold)] underline-offset-4 hover:underline">
            token system
          </Link>{' '}
          page renders, so this page cannot drift from it or from{' '}
          <code className="font-mono text-[0.85em] text-[var(--mustard)]">index.css</code>. It used to
          keep its own hand-typed list.
        </p>
      </ProductDesignSection>

      <ProductDesignSection title="Bridging diagrams & facilitation walls" hint="Static HTML/SVG canvases reuse the semantic accent system documented in assets/visual-language.">
        <p className="text-sm leading-relaxed text-[var(--text-sec)]">
          Narrative canvases authored in Peak stay valid for metaphors — when those assets appear
          inside priorities.ai viewers, remap fills to tinted zones (--slate-dim / --mustard-dim)
          rather than luminous slide gradients. Canonical HTML reference: the{' '}
          <a
            href="/visual-language/field-guide.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--p-gold)] underline-offset-4 hover:underline"
          >
            visual language field guide
          </a>{' '}
          and viewer shell, for interplay with this token sheet.
        </p>
      </ProductDesignSection>

      <ProductDesignSection
        title="Older colors"
        hint="Deprecated. Not part of either palette."
      >
        <p className="text-sm leading-relaxed text-[var(--text-sec)]">
          The sage green family, Electric Mustard, Luminous Coral, Alpine and Granite are deprecated.
          They are not shown on this page. Parts of the app's code still use them, so they are kept
          on the record on the{' '}
          <Link to="/elevation/priorities-ai-design-language/legacy" className="text-[var(--p-gold)] underline-offset-4 hover:underline">
            Legacy colors and marks
          </Link>{' '}
          page until the app stops depending on them.
        </p>
      </ProductDesignSection>
    </>
  );
};
